import type { LucideIcon } from "lucide-react";
import { CircleCheck, FileQuestion, Info, TriangleAlert } from "lucide-react";

import { cn } from "@/lib/utils";

type StatusPanelTone = "neutral" | "info" | "success" | "warning" | "destructive";
type StatusPanelSize = "compact" | "panel" | "page";

/** The glyph's colour is the whole of the tone: there is no tinted plate behind it. */
const toneIconClass: Record<StatusPanelTone, string> = {
  neutral: "text-muted-foreground",
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  destructive: "text-destructive",
};

/**
 * One glyph per tone, so the pairing is decided here rather than at each call site.
 * A panel whose subject has a better glyph (a folder, a shield) passes `icon`.
 */
const toneIcon: Record<StatusPanelTone, LucideIcon> = {
  neutral: FileQuestion,
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  destructive: TriangleAlert,
};

/**
 * `compact` titles a region the screen around it still explains, so it stays a paragraph and out
 * of the heading outline. The other two stand in for the whole screen — a person landing on
 * "Page not found" or "Admin access required" is reading that page's heading, and a screen reader
 * navigating by headings has to be able to reach it.
 */
const titleTag = { compact: "p", panel: "h2", page: "h2" } as const;

const sizeClasses: Record<
  StatusPanelSize,
  { box: string; icon: string; title: string; description: string; actions: string }
> = {
  // No `h-full` here: a compact panel sits in the flow of a sidebar or a card, not in a box of
  // its own, and stretching to the parent's height would push everything below it off-screen.
  compact: {
    box: "px-4 py-10",
    icon: "mb-2 size-7",
    title: "font-medium text-sm",
    description: "mt-1 text-xs",
    actions: "mt-3",
  },
  panel: {
    box: "h-full min-h-[240px] p-6",
    icon: "mb-3 size-6",
    title: "font-medium text-sm",
    description: "mt-1 text-sm",
    actions: "mt-4",
  },
  page: {
    box: "h-full min-h-[60vh] p-6",
    icon: "mb-4 size-8",
    title: "font-semibold text-xl",
    description: "mt-2 text-sm leading-relaxed",
    actions: "mt-6",
  },
};

interface StatusPanelProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** What the panel says, as a sentence or a short heading. The one required prop. */
  title: string;
  /** The supporting line: what happened, or what to do about it. */
  description?: React.ReactNode;
  /** Colours the glyph and picks its default. */
  tone?: StatusPanelTone;
  /** `panel` fills a tab, card or route body; `page` is for a whole viewport. */
  size?: StatusPanelSize;
  /** Overrides the tone's glyph where the subject has a better one (`FolderX`, `ShieldAlert`). */
  icon?: LucideIcon;
  /**
   * Colours the glyph from a **domain ramp** the tone vocabulary cannot express — Quality Guard
   * severity, and nothing else. Never a colour chosen by hand: a panel that needs a hue the five
   * tones do not carry needs a token, not a class here.
   */
  iconClassName?: string;
  /** Draws the glyph. `false` drops it, for a panel the heading above already introduces. */
  showIcon?: boolean;
  /** A short label above the title, such as an HTTP status. */
  eyebrow?: string;
  /** Recovery controls, laid out in a row. */
  actions?: React.ReactNode;
  /** Anything below the actions — a support id, a details disclosure. */
  children?: React.ReactNode;
}

/**
 * StatusPanel — the one centered message the app shows in place of content.
 *
 * Every "this failed", "not found", "you do not have access" and "nothing to show here" renders
 * this, so the glyph, the type scale and the spacing between them are decided once. The shape is
 * fixed: a **bare** glyph, the title, the description, then the actions. The tone is carried by
 * the glyph's colour alone. There is no tinted plate behind the icon and no variant that adds
 * one, and the title never takes the tone: a title that turns red beside a red glyph is a title
 * nobody can read at a glance.
 *
 * The tone names are the design system's status vocabulary, the same four `Badge` and `Alert`
 * carry, plus `neutral` for a message that signals nothing — an absence rather than a failure.
 * `destructive` is this system's word for "this failed" (`.design-sync/conventions.md`).
 *
 * `panel` is the default because most of these fill a tab, a card or a route body; `page` is for
 * the few that own a whole viewport (the global 404, the root error boundary), where the panel
 * scale reads as lost in the empty space. Height and flex behaviour beyond the minimum are the
 * caller's, through `className` (`flex-1`, `h-full`).
 *
 * `EmptyState` is the inline sibling for a list with no rows, and `Alert` the inline sibling for
 * a notice that sits *beside* content rather than in place of it.
 *
 * @example
 * ```tsx
 * <StatusPanel
 *   tone="destructive"
 *   title="This admin page failed to load."
 *   description="The error is contained to this admin tab — other pages are unaffected."
 *   actions={
 *     <Button variant="outline" size="sm" onClick={resetError}>
 *       <RefreshCw data-icon="inline-start" />
 *       Try again
 *     </Button>
 *   }
 * />
 * ```
 */
function StatusPanel({
  title,
  description,
  tone = "neutral",
  size = "panel",
  icon,
  iconClassName,
  showIcon = true,
  eyebrow,
  actions,
  children,
  className,
  ...props
}: StatusPanelProps) {
  const Icon = icon ?? toneIcon[tone];
  const scale = sizeClasses[size];
  const Title = titleTag[size];

  return (
    <div className={cn("flex w-full items-center justify-center", scale.box, className)} {...props}>
      <div className="max-w-md text-center">
        {showIcon && (
          <Icon
            aria-hidden="true"
            className={cn("mx-auto", scale.icon, iconClassName ?? toneIconClass[tone])}
          />
        )}
        {eyebrow && <p className="mb-1 font-medium text-muted-foreground text-sm">{eyebrow}</p>}
        <Title className={cn("text-foreground", scale.title)}>{title}</Title>
        {description && (
          <p className={cn("text-muted-foreground", scale.description)}>{description}</p>
        )}
        {actions && (
          <div className={cn("flex items-center justify-center gap-2", scale.actions)}>
            {actions}
          </div>
        )}
        {children && <div className="mt-3">{children}</div>}
      </div>
    </div>
  );
}

export { StatusPanel };
export type { StatusPanelProps, StatusPanelTone, StatusPanelSize };
